import {
  For,
  Index,
  Show,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";
import { useNavigate, useSearchParams } from "@solidjs/router";
import { api } from "../api/client";
import { loadTemplates } from "../lib/data";
import type { Exercise, ExerciseHistoryItem } from "../api/types";
import ActiveWorkoutTimerBar from "../components/ActiveWorkoutTimerBar";
import BottomSheet from "../components/BottomSheet";
import ActivityFields from "../components/ActivityFields";
import DecimalField from "../components/DecimalField";
import DragHandle from "../components/DragHandle";
import ExerciseIcon from "../components/ExerciseIcon";
import ExerciseFullScreen from "../components/ExerciseFullScreen";
import { t } from "../i18n";
import { lastTimeHint, metricParts } from "../lib/activity";
import {
  type ActiveExercise,
  type ActiveSet,
  type ActiveWorkoutSession,
  activeSessionSignal,
  activeType,
  exercisesMissingValues,
  bankAndResetExercise,
  buildPlayOrder,
  clearActiveSession,
  createBlankExercise,
  createCircuit,
  createEmptySet,
  durationMinutesFromSession,
  exerciseAllSetsDone,
  findPlayStepIndex,
  getActiveSession,
  isWorkoutPaused,
  pauseActiveWorkout,
  resumeActiveWorkout,
  startActiveSession,
  toApiExercises,
  updateActiveSession,
} from "../lib/activeWorkout";
import { createListDrag } from "../lib/listDrag";
import { moveItem } from "../lib/reorder";
import {
  REST_PRESETS,
  resetRestTimer,
  restTimerState,
  startRestTimer,
} from "../lib/restTimer";
import {
  maybeCelebrateStreakIncrease,
  showSessionCelebration,
} from "../lib/streakCelebration";
import { tgAlert, tgConfirm, tgHapticImpact } from "../lib/telegram";
import { useAuth } from "../stores/auth";

const VALUE_KEYS = ["durationSec", "distanceM", "intensity", "rounds", "invalid"] as const;

/** The activity value fields of an ActivityFields patch (ActiveExercise has no sets/notes there). */
function valuesOnly(patch: Partial<Exercise>): Partial<ActiveExercise> {
  const out: Record<string, unknown> = {};
  for (const k of VALUE_KEYS) if (k in patch) out[k] = patch[k];
  return out as Partial<ActiveExercise>;
}

const WEIGHT_STEP = 2.5;
const REPS_STEP = 1;
const AUTO_ADVANCE_MS = 700;

export default function ActiveWorkoutPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const auth = useAuth();
  const session = activeSessionSignal();

  const [boot, setBoot] = createSignal<"loading" | "ready" | "error">(
    "loading",
  );
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [now, setNow] = createSignal(Date.now());
  const [historyCache, setHistoryCache] = createSignal<
    Record<string, ExerciseHistoryItem[]>
  >({});
  const [historyLoading, setHistoryLoading] = createSignal<string | null>(null);
  const [listOpen, setListOpen] = createSignal(false);
  const [animKey, setAnimKey] = createSignal(0);
  const [showAdvanceHint, setShowAdvanceHint] = createSignal(false);
  const [notesOpen, setNotesOpen] = createSignal(false);
  const [showValueErrors, setShowValueErrors] = createSignal(false);
  const [customRestOpen, setCustomRestOpen] = createSignal(false);
  /** Free-typed custom rest seconds (string so partial input works). */
  const [customRestDraft, setCustomRestDraft] = createSignal("90");

  let advanceTimer: ReturnType<typeof setTimeout> | undefined;
  /** True when this page pushed the `?edit=` history entry (so closing can pop it). */
  let editorPushed = false;
  /** Set while we are closing the editor ourselves (so the stale-link cleanup stays out of the way). */
  let editorClosing = false;

  const drag = createListDrag({
    onReorder: (from, to) => {
      const cur = session();
      if (!cur) return;
      updateActiveSession({
        exercises: moveItem(cur.exercises, from, to),
      });
    },
  });

  // Live workout clock (frozen while paused)
  createEffect(() => {
    if (!session()) return;
    if (isWorkoutPaused(session())) {
      setNow(Date.now());
      return;
    }
    const id = setInterval(() => setNow(Date.now()), 1000);
    onCleanup(() => clearInterval(id));
  });

  onCleanup(() => {
    if (advanceTimer) clearTimeout(advanceTimer);
  });

  // Bootstrap once: resume session or seed from planned/template/query
  onMount(() => {
    let cancelled = false;

    (async () => {
      const fromPlanned = (search.fromPlanned as string) || "";
      const fromTemplate = (search.fromTemplate as string) || "";
      const forceNew =
        search.new === "1" ||
        search.new === "true" ||
        search.start === "1";

      try {
        const existing = getActiveSession();

        if (existing && !fromPlanned && !fromTemplate && !forceNew) {
          if (!cancelled) setBoot("ready");
          return;
        }

        if (existing && (fromPlanned || fromTemplate || forceNew)) {
          const ok = await tgConfirm(t("ACTIVE_REPLACE_CONFIRM"));
          if (!ok) {
            if (!cancelled) {
              setBoot("ready");
              navigate("/workouts/active", { replace: true });
            }
            return;
          }
          clearActiveSession();
        }

        if (fromPlanned) {
          const w = await api.getPlanned(fromPlanned);
          if (cancelled) return;
          startActiveSession({
            force: true,
            title: w.title,
            notes: w.notes ?? "",
            plannedId: w.id,
            sourceLabel: w.title,
            exercises: w.exercises,
          });
          navigate("/workouts/active", { replace: true });
          setBoot("ready");
          return;
        }

        if (fromTemplate) {
          const list = await loadTemplates();
          if (cancelled) return;
          const tpl = list.find((x) => x.id === fromTemplate);
          if (!tpl) {
            setError(t("ERROR_GENERIC"));
            startActiveSession({ force: true });
          } else {
            startActiveSession({
              force: true,
              title: tpl.name,
              sourceLabel: tpl.name,
              exercises: tpl.exercises,
            });
          }
          navigate("/workouts/active", { replace: true });
          setBoot("ready");
          return;
        }

        if (!getActiveSession()) {
          startActiveSession({ force: true });
        }
        if (forceNew) {
          navigate("/workouts/active", { replace: true });
        }
        setBoot("ready");
      } catch {
        if (cancelled) return;
        setError(t("ERROR_GENERIC"));
        if (!getActiveSession()) startActiveSession({ force: true });
        setBoot("ready");
      }
    })();

    onCleanup(() => {
      cancelled = true;
    });
  });

  const s = () => session();

  function patchSession(
    patch: Partial<
      Pick<
        ActiveWorkoutSession,
        | "title"
        | "notes"
        | "exercises"
        | "circuits"
        | "focusExerciseId"
        | "focusRound"
      >
    >,
  ) {
    updateActiveSession(patch);
  }

  function updateExercise(exId: string, patch: Partial<ActiveExercise>) {
    const cur = s();
    if (!cur) return;
    patchSession({
      exercises: cur.exercises.map((ex) =>
        ex.id === exId ? { ...ex, ...patch } : ex,
      ),
    });
  }

  function updateSet(
    exId: string,
    setId: string,
    patch: Partial<ActiveSet>,
  ) {
    const cur = s();
    if (!cur) return;
    patchSession({
      exercises: cur.exercises.map((ex) => {
        if (ex.id !== exId) return ex;
        return {
          ...ex,
          sets: ex.sets.map((st) =>
            st.id === setId ? { ...st, ...patch } : st,
          ),
        };
      }),
    });
  }

  /** `new` opens a blank exercise; an id opens that exercise; anything else = closed. */
  const editKey = createMemo(() => {
    const k = search.edit;
    const v = Array.isArray(k) ? k[0] : k;
    if (!v || boot() !== "ready") return null;
    if (v === "new") return "new";
    return s()?.exercises.some((e) => e.id === v) ? v : null;
  });

  const playOrder = createMemo(() => {
    const cur = s();
    return cur ? buildPlayOrder(cur) : [];
  });

  const playIndex = createMemo(() => {
    const cur = s();
    if (!cur) return -1;
    return findPlayStepIndex(
      playOrder(),
      cur.focusExerciseId,
      cur.focusRound || 1,
    );
  });

  const currentStep = createMemo(() => {
    const idx = playIndex();
    if (idx < 0) return null;
    return playOrder()[idx] ?? null;
  });

  const focusExercise = createMemo(() => {
    const cur = s();
    const step = currentStep();
    if (!cur || !step) return null;
    return cur.exercises.find((e) => e.id === step.exerciseId) ?? null;
  });

  const nextStep = createMemo(() => {
    const idx = playIndex();
    if (idx < 0) return null;
    return playOrder()[idx + 1] ?? null;
  });

  const nextExercise = createMemo(() => {
    const cur = s();
    const step = nextStep();
    if (!cur || !step) return null;
    return cur.exercises.find((e) => e.id === step.exerciseId) ?? null;
  });

  function goToStep(exerciseId: string, round: number, animate = true) {
    const cur = s();
    if (!cur) return;
    const fromId = cur.focusExerciseId;
    const fromRound = cur.focusRound || 1;
    let exercises = cur.exercises;

    // Leaving a circuit exercise for a later round of the same (or next) step:
    // bank completed sets so totals accumulate across rounds.
    if (fromId && (fromId !== exerciseId || fromRound !== round)) {
      const leaving = exercises.find((e) => e.id === fromId);
      if (leaving && leaving.circuitId && exerciseAllSetsDone(leaving)) {
        exercises = exercises.map((e) =>
          e.id === fromId ? bankAndResetExercise(e) : e,
        );
      }
    }

    patchSession({
      exercises,
      focusExerciseId: exerciseId,
      focusRound: Math.max(1, round),
    });
    setShowAdvanceHint(false);
    setNotesOpen(false);
    if (animate) setAnimKey((k) => k + 1);
  }

  function goToNext() {
    const n = nextStep();
    if (!n) return;
    goToStep(n.exerciseId, n.round);
    tgHapticImpact("light");
  }

  function goToPrev() {
    const idx = playIndex();
    if (idx <= 0) return;
    const prev = playOrder()[idx - 1];
    if (!prev) return;
    goToStep(prev.exerciseId, prev.round);
  }

  function scheduleAdvanceIfComplete(exId: string) {
    if (advanceTimer) clearTimeout(advanceTimer);
    const cur = getActiveSession();
    if (!cur) return;
    const ex = cur.exercises.find((e) => e.id === exId);
    if (!ex || !exerciseAllSetsDone(ex)) {
      setShowAdvanceHint(false);
      return;
    }
    if (!nextStep()) {
      setShowAdvanceHint(false);
      return;
    }
    setShowAdvanceHint(true);
    advanceTimer = setTimeout(() => {
      const latest = getActiveSession();
      if (!latest) return;
      const still = latest.exercises.find((e) => e.id === exId);
      if (still && exerciseAllSetsDone(still) && latest.focusExerciseId === exId) {
        goToNext();
      }
    }, AUTO_ADVANCE_MS);
  }

  function toggleSetDone(exId: string, setId: string) {
    const cur = s();
    if (!cur) return;
    const ex = cur.exercises.find((e) => e.id === exId);
    const st = ex?.sets.find((x) => x.id === setId);
    if (!st) return;
    const nextDone = !st.done;
    updateSet(exId, setId, { done: nextDone });
    if (nextDone) {
      tgHapticImpact("medium");
      // Rest between sets is a strength thing.
      if (ex && activeType(ex) === "strength") startRestTimer();
      // Check after state commit
      queueMicrotask(() => scheduleAdvanceIfComplete(exId));
    } else {
      setShowAdvanceHint(false);
      if (advanceTimer) clearTimeout(advanceTimer);
    }
  }

  function nudgeWeight(exId: string, setId: string, delta: number) {
    const cur = s();
    const ex = cur?.exercises.find((e) => e.id === exId);
    const st = ex?.sets.find((x) => x.id === setId);
    if (!st) return;
    const base = st.weight ?? 0;
    const next = Math.max(0, Math.round((base + delta) * 2) / 2);
    updateSet(exId, setId, { weight: next === 0 && st.weight == null ? 0 : next });
  }

  function nudgeReps(exId: string, setId: string, delta: number) {
    const cur = s();
    const ex = cur?.exercises.find((e) => e.id === exId);
    const st = ex?.sets.find((x) => x.id === setId);
    if (!st) return;
    const next = Math.max(1, Math.round(st.reps + delta));
    updateSet(exId, setId, { reps: next });
  }

  function addSet(exId: string) {
    const cur = s();
    if (!cur) return;
    const ex = cur.exercises.find((e) => e.id === exId);
    if (!ex) return;
    const last = ex.sets[ex.sets.length - 1];
    updateExercise(exId, {
      sets: [
        ...ex.sets,
        createEmptySet({
          reps: last?.reps ?? 10,
          weight: last?.weight ?? null,
          unit: last?.unit ?? "kg",
        }),
      ],
    });
  }

  function removeSet(exId: string, setId: string) {
    const cur = s();
    if (!cur) return;
    const ex = cur.exercises.find((e) => e.id === exId);
    if (!ex || ex.sets.length <= 1) return;
    updateExercise(exId, {
      sets: ex.sets.filter((st) => st.id !== setId),
    });
  }

  /** The full-screen editor lives in the URL (`?edit=<id>|new`) so back/Esc/BackButton close it. */
  function openEditor(target: string) {
    editorPushed = true;
    setListOpen(false);
    navigate(`/workouts/active?edit=${encodeURIComponent(target)}`);
  }

  function closeEditor() {
    editorClosing = true;
    if (editorPushed) {
      navigate(-1);
    } else {
      navigate("/workouts/active", { replace: true });
    }
  }

  function addExercise() {
    openEditor("new");
  }

  function openList() {
    setListOpen(true);
  }

  function goToExerciseFromList(exId: string) {
    const cur = s();
    const round =
      cur?.focusExerciseId === exId ? cur.focusRound || 1 : 1;
    goToStep(exId, round, true);
    setListOpen(false);
  }

  function saveEditor(isNew: boolean, ex: ActiveExercise) {
    const cur = s();
    if (!cur) return;
    if (isNew) {
      const wasEmpty = cur.exercises.length === 0;
      patchSession({
        exercises: [...cur.exercises, ex],
        focusExerciseId: wasEmpty ? ex.id : cur.focusExerciseId,
        focusRound: wasEmpty ? 1 : cur.focusRound,
      });
    } else {
      patchSession({
        exercises: cur.exercises.map((e) => (e.id === ex.id ? ex : e)),
      });
    }
    closeEditor();
  }

  function deleteFromEditor(exId: string) {
    closeEditor();
    removeExerciseNow(exId);
  }

  async function removeExercise(exId: string) {
    if (!(await tgConfirm(t("CONFIRM_DELETE")))) return;
    removeExerciseNow(exId);
  }

  function removeExerciseNow(exId: string) {
    const cur = s();
    if (!cur) return;
    const next = cur.exercises.filter((ex) => ex.id !== exId);
    // Prune empty circuits
    const used = new Set(next.map((e) => e.circuitId).filter(Boolean));
    const circuits = cur.circuits.filter((c) => used.has(c.id));
    let focusExerciseId = cur.focusExerciseId;
    let focusRound = cur.focusRound;
    if (focusExerciseId === exId) {
      focusExerciseId = next[0]?.id ?? null;
      focusRound = 1;
    }
    patchSession({ exercises: next, circuits, focusExerciseId, focusRound });
  }

  function toggleInCircuit(exId: string) {
    const cur = s();
    if (!cur) return;
    const idx = cur.exercises.findIndex((e) => e.id === exId);
    if (idx < 0) return;
    const ex = cur.exercises[idx]!;
    if (ex.circuitId) {
      // Leave circuit
      const cid = ex.circuitId;
      const exercises = cur.exercises.map((e) =>
        e.id === exId ? { ...e, circuitId: null } : e,
      );
      const still = exercises.some((e) => e.circuitId === cid);
      const circuits = still
        ? cur.circuits
        : cur.circuits.filter((c) => c.id !== cid);
      patchSession({ exercises, circuits });
      return;
    }
    // Join neighboring circuit, or create new with previous/next if any
    const prev = cur.exercises[idx - 1];
    const next = cur.exercises[idx + 1];
    let circuitId = prev?.circuitId || next?.circuitId || null;
    let circuits = cur.circuits;
    if (!circuitId) {
      const c = createCircuit(3);
      circuits = [...circuits, c];
      circuitId = c.id;
      // Also pull adjacent standalone neighbors into same new circuit? only this one for now.
    }
    patchSession({
      circuits,
      exercises: cur.exercises.map((e) =>
        e.id === exId ? { ...e, circuitId } : e,
      ),
    });
  }

  function setCircuitRounds(circuitId: string, rounds: number) {
    const cur = s();
    if (!cur) return;
    const r = Math.max(1, Math.min(20, Math.round(rounds)));
    patchSession({
      circuits: cur.circuits.map((c) =>
        c.id === circuitId ? { ...c, rounds: r } : c,
      ),
    });
  }

  /** Merge this exercise with the previous into a shared circuit. */
  function linkWithPrevious(exId: string) {
    const cur = s();
    if (!cur) return;
    const idx = cur.exercises.findIndex((e) => e.id === exId);
    if (idx <= 0) return;
    const prev = cur.exercises[idx - 1]!;
    const ex = cur.exercises[idx]!;
    let circuits = cur.circuits;
    let circuitId = prev.circuitId || ex.circuitId;
    if (!circuitId) {
      const c = createCircuit(3);
      circuits = [...circuits, c];
      circuitId = c.id;
    } else if (prev.circuitId && ex.circuitId && prev.circuitId !== ex.circuitId) {
      // Absorb ex's circuit into prev's
      const drop = ex.circuitId;
      circuitId = prev.circuitId;
      circuits = circuits.filter((c) => c.id !== drop);
    }
    const cid = circuitId;
    patchSession({
      circuits,
      exercises: cur.exercises.map((e) => {
        if (e.id === prev.id || e.id === ex.id) return { ...e, circuitId: cid };
        if (e.circuitId === ex.circuitId && ex.circuitId && ex.circuitId !== cid) {
          return { ...e, circuitId: cid };
        }
        return e;
      }),
    });
  }

  async function loadHistory(name: string) {
    const key = name.trim().toLowerCase();
    if (!key) return;
    if (historyCache()[key]) return;
    setHistoryLoading(key);
    try {
      const items = await api.exerciseHistory(name.trim(), 5);
      setHistoryCache((c) => ({ ...c, [key]: items }));
    } catch {
      setHistoryCache((c) => ({ ...c, [key]: [] }));
    } finally {
      setHistoryLoading(null);
    }
  }

  createEffect(() => {
    const ex = focusExercise();
    const n = ex?.name.trim();
    if (n) void loadHistory(n);
  });

  // `?edit=<unknown id>` (stale link): drop it.
  createEffect(() => {
    if (!search.edit) {
      editorClosing = false;
      editorPushed = false;
      return;
    }
    if (boot() !== "ready" || editorClosing) return;
    if (!editKey()) navigate("/workouts/active", { replace: true });
  });

  // Ensure focus points at something valid
  createEffect(() => {
    const cur = s();
    if (!cur || boot() !== "ready") return;
    if (!cur.exercises.length) {
      if (cur.focusExerciseId) {
        patchSession({ focusExerciseId: null, focusRound: 1 });
      }
      return;
    }
    if (
      !cur.focusExerciseId ||
      !cur.exercises.some((e) => e.id === cur.focusExerciseId)
    ) {
      patchSession({
        focusExerciseId: cur.exercises[0]!.id,
        focusRound: 1,
      });
    }
  });

  async function discard() {
    if (!(await tgConfirm(t("ACTIVE_DISCARD_CONFIRM")))) return;
    resetRestTimer();
    clearActiveSession();
    navigate("/", { replace: true });
  }

  async function finish() {
    const cur = s();
    if (!cur || saving()) return;
    const missing = exercisesMissingValues(cur.exercises);
    if (missing.length) {
      setShowValueErrors(true);
      await tgAlert(`${t("ACTIVE_NEED_VALUES")}: ${missing.join(", ")}`);
      return;
    }
    const exercises = toApiExercises(cur.exercises);
    if (!exercises.length) {
      await tgAlert(t("ACTIVE_NEED_EXERCISE"));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (cur.pausedAt) resumeActiveWorkout();
      const latest = getActiveSession() ?? cur;
      const finishedAt = new Date();
      const durationMinutes = durationMinutesFromSession(latest, finishedAt);
      const title = cur.title.trim() || t("ACTIVE_WORKOUT");
      const body = {
        title,
        notes: cur.notes.trim() || null,
        performedAt: finishedAt.toISOString(),
        durationMinutes,
        startedAt: latest.startedAt,
        finishedAt: finishedAt.toISOString(),
        exercises,
      };

      if (latest.plannedId) {
        await api.completePlanned(latest.plannedId, {
          title: body.title,
          notes: body.notes,
          performedAt: body.performedAt,
          durationMinutes: body.durationMinutes,
          exercises,
        });
      } else {
        await api.createCompleted(body);
      }

      const prevStreak = auth.profile()?.streak ?? 0;
      await auth.refreshProfile().catch(() => undefined);
      const streakGrew = maybeCelebrateStreakIncrease(
        prevStreak,
        auth.profile()?.streak,
      );
      resetRestTimer();
      clearActiveSession();
      if (!streakGrew) {
        showSessionCelebration(durationMinutes, title);
        tgHapticImpact("heavy");
      }
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("ERROR_GENERIC"));
      await tgAlert(err instanceof Error ? err.message : t("ERROR_GENERIC"));
    } finally {
      setSaving(false);
    }
  }

  const doneCount = createMemo(() => {
    const cur = s();
    if (!cur) return 0;
    return cur.exercises.reduce((n, ex) => {
      const current = ex.sets.filter((st) => st.done).length;
      return n + (ex.bankedSets || 0) + current;
    }, 0);
  });

  const totalSets = createMemo(() => {
    const cur = s();
    if (!cur) return 0;
    // Approximate planned sets: per exercise sets × circuit rounds
    const roundsById = new Map(cur.circuits.map((c) => [c.id, c.rounds]));
    return cur.exercises.reduce((n, ex) => {
      const mult = ex.circuitId
        ? Math.max(1, roundsById.get(ex.circuitId) ?? 1)
        : 1;
      return n + ex.sets.length * mult;
    }, 0);
  });

  const restState = restTimerState();
  const lastRestPreset = () => {
    const n = restState().lastPreset;
    return Number.isFinite(n) && n > 0 ? n : 90;
  };

  function startCustomRest() {
    const n = Math.round(Number(customRestDraft()));
    if (!Number.isFinite(n) || n < 1) return;
    startRestTimer(Math.min(3600, n));
    setCustomRestOpen(false);
  }

  function circuitRounds(circuitId: string | null | undefined) {
    if (!circuitId) return 1;
    const cur = s();
    return cur?.circuits.find((c) => c.id === circuitId)?.rounds ?? 1;
  }

  function isExerciseCompleteInList(ex: ActiveExercise): boolean {
    const rounds = circuitRounds(ex.circuitId);
    if (!ex.circuitId || rounds <= 1) {
      return exerciseAllSetsDone(ex);
    }
    // All rounds banked + current visit done, or banked covers all planned
    const planned = ex.sets.length * rounds;
    const done = (ex.bankedSets || 0) + ex.sets.filter((st) => st.done).length;
    return done >= planned;
  }

  return (
    <div class="active-workout aw-focus">
      <Show when={boot() === "loading"}>
        <p class="py-12 text-center text-sm text-ink-muted">{t("LOADING")}</p>
      </Show>

      <Show when={boot() === "ready" && s()}>
        {(cur) => (
          <>
            <ActiveWorkoutTimerBar
              session={cur()}
              now={now()}
              doneCount={doneCount()}
              totalSets={totalSets()}
            />

            {/* Compact header */}
            <header class="flex items-center justify-between gap-2">
              <div class="min-w-0 flex-1">
                <input
                  class="w-full bg-transparent text-[22px] font-semibold tracking-[-0.03em] text-ink outline-none placeholder:text-ink-muted"
                  value={cur().title}
                  placeholder={t("TITLE")}
                  onInput={(e) =>
                    patchSession({ title: e.currentTarget.value })
                  }
                />
              </div>
              <div class="flex shrink-0 items-center gap-1">
                <Show
                  when={!isWorkoutPaused(cur())}
                  fallback={
                    <button
                      type="button"
                      class="btn-primary !px-3 !py-2 text-xs"
                      onClick={() => resumeActiveWorkout()}
                    >
                      {t("WORKOUT_RESUME")}
                    </button>
                  }
                >
                  <button
                    type="button"
                    class="btn-ghost !px-2.5 !py-2 text-xs text-ink-muted"
                    onClick={() => pauseActiveWorkout()}
                  >
                    {t("WORKOUT_PAUSE")}
                  </button>
                </Show>
                <button
                  type="button"
                  class="btn-ghost !px-2.5 !py-2 text-xs text-ink-muted"
                  onClick={() => void discard()}
                >
                  {t("ACTIVE_DISCARD")}
                </button>
              </div>
            </header>

            <Show when={isWorkoutPaused(cur())}>
              <p class="rounded-[12px] bg-[var(--surface)] px-4 py-3 text-[15px] text-[var(--destructive)]">
                {t("WORKOUT_PAUSED_HINT")}
              </p>
            </Show>

            {/* Compact rest presets + custom duration */}
            <div class="aw-rest-presets aw-rest-presets--compact">
              <div class="flex flex-wrap items-center gap-1.5">
                <span class="mr-0.5 text-[13px] text-ink-muted">
                  {t("REST_TIMER")}
                </span>
                <For each={[...REST_PRESETS]}>
                  {(sec) => (
                    <button
                      type="button"
                      class={
                        lastRestPreset() === sec && !customRestOpen()
                          ? "tg-chip is-active"
                          : "tg-chip"
                      }
                      onClick={() => {
                        setCustomRestOpen(false);
                        startRestTimer(sec);
                      }}
                    >
                      {sec >= 60 ? `${sec / 60}m` : `${sec}s`}
                    </button>
                  )}
                </For>
                <button
                  type="button"
                  class={customRestOpen() ? "tg-chip is-active" : "tg-chip"}
                  onClick={() => {
                    setCustomRestDraft(String(lastRestPreset()));
                    setCustomRestOpen((v) => !v);
                  }}
                >
                  {t("REST_CUSTOM")}
                </button>
                <button
                  type="button"
                  class="rounded-full border border-accent/40 bg-accent/15 px-2.5 py-1.5 text-[11px] font-semibold text-accent"
                  onClick={() => {
                    if (customRestOpen()) startCustomRest();
                    else startRestTimer();
                  }}
                >
                  {t("REST_START")}
                </button>
              </div>
              <Show when={customRestOpen()}>
                <div class="mt-2 flex items-center gap-2">
                  <input
                    class="input flex-1 tabular-nums !py-2 text-sm"
                    type="text"
                    inputmode="numeric"
                    enterkeyhint="done"
                    autocomplete="off"
                    value={customRestDraft()}
                    placeholder="90"
                    aria-label={t("REST_CUSTOM")}
                    onInput={(e) => {
                      const raw = e.currentTarget.value.replace(/[^\d]/g, "");
                      if (raw === "") {
                        setCustomRestDraft("");
                        return;
                      }
                      const n = Number(raw);
                      if (!Number.isFinite(n)) return;
                      setCustomRestDraft(String(Math.min(3600, n)));
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      e.preventDefault();
                      startCustomRest();
                    }}
                  />
                  <span class="shrink-0 text-xs text-ink-muted">s</span>
                  <button
                    type="button"
                    class="btn-primary !py-2 text-xs"
                    onClick={() => startCustomRest()}
                  >
                    {t("REST_START")}
                  </button>
                </div>
              </Show>
            </div>

            <Show when={error()}>
              <p class="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
                {error()}
              </p>
            </Show>

            <Show
              when={focusExercise()}
              fallback={
                <div class="aw-focus-empty">
                  <p class="text-center text-sm text-ink-muted">
                    {t("ACTIVE_EMPTY")}
                  </p>
                  <button
                    type="button"
                    class="btn-primary mt-4 w-full !py-3"
                    onClick={() => addExercise()}
                  >
                    + {t("ADD_EXERCISE")}
                  </button>
                </div>
              }
            >
              {(ex) => {
                const step = () => currentStep();
                const histKey = () => ex().name.trim().toLowerCase();
                const history = () => historyCache()[histKey()] ?? [];
                const firstOpenSet = () =>
                  ex().sets.find((st) => !st.done) ??
                  ex().sets[ex().sets.length - 1];

                return (
                  <div
                    class="aw-focus-stage"
                    data-anim={animKey()}
                  >
                    {/* Round / progress */}
                    <div class="aw-focus-meta">
                      <div class="flex flex-wrap items-center gap-1.5">
                        <Show when={step()?.circuitId}>
                          <span class="aw-focus-round">
                            {t("CIRCUIT_ROUND")
                              .replace("{n}", String(step()!.round))
                              .replace(
                                "{total}",
                                String(step()!.totalRounds),
                              )}
                          </span>
                          <span class="aw-focus-circuit-tag">
                            {t("CIRCUIT_IN")}
                          </span>
                        </Show>
                      </div>
                      <span class="aw-focus-progress tabular-nums">
                        {Math.max(1, playIndex() + 1)}/{playOrder().length}
                      </span>
                    </div>

                    {/* Exercise name — huge */}
                    <div class="aw-focus-name-wrap">
                      <Show
                        when={ex().name.trim()}
                        fallback={
                          <button
                            type="button"
                            class="aw-focus-pick"
                            onClick={() => openEditor(ex().id)}
                          >
                            {t("FIND_EXERCISE")}
                          </button>
                        }
                      >
                        <h2 class="aw-focus-name">{ex().name}</h2>
                        <button
                          type="button"
                          class="aw-focus-rename"
                          onClick={() => openEditor(ex().id)}
                        >
                          {t("EDIT")}
                        </button>
                      </Show>
                    </div>

                    <Show
                      when={
                        activeType(ex()) !== "strength" &&
                        lastTimeHint(history(), activeType(ex()))
                      }
                    >
                      {(hint) => <p class="ex-last-time aw-last-time">{hint()}</p>}
                    </Show>

                    {/* Previous results — compact */}
                    <Show when={ex().name.trim() && activeType(ex()) === "strength"}>
                      <div class="aw-focus-history">
                        <Show when={historyLoading() === histKey()}>
                          <span class="text-ink-muted">{t("LOADING")}</span>
                        </Show>
                        <Show
                          when={
                            historyLoading() !== histKey() &&
                            history().length === 0
                          }
                        >
                          <span class="text-ink-muted">{t("NO_HISTORY")}</span>
                        </Show>
                        <Show when={history().length > 0}>
                          <For each={history().slice(0, 3)}>
                            {(h) => (
                              <span class="aw-focus-history-item tabular-nums">
                                {h.sets}×{h.reps}
                                {h.weight != null ? ` · ${h.weight}` : ""}
                              </span>
                            )}
                          </For>
                        </Show>
                      </div>
                    </Show>

                    <Show
                      when={activeType(ex()) === "strength"}
                      fallback={
                        <div class="aw-focus-sets space-y-3">
                          <div class="rounded-[12px] bg-[var(--surface)] p-3 space-y-3">
                            <ActivityFields
                              ex={{
                                name: ex().name,
                                sets: 1,
                                reps: 1,
                                weight: null,
                                exerciseType: activeType(ex()),
                                durationSec: ex().durationSec,
                                distanceM: ex().distanceM,
                                intensity: ex().intensity,
                                rounds: ex().rounds,
                                invalid: ex().invalid,
                              }}
                              showErrors={showValueErrors()}
                              onPatch={(patch) => updateExercise(ex().id, valuesOnly(patch))}
                            />
                          </div>
                          <div
                            class={`aw-set-row ${ex().sets[0]?.done ? "is-done" : ""}`}
                            onClick={(e) => {
                              if ((e.target as HTMLElement).closest("button")) return;
                              toggleSetDone(ex().id, ex().sets[0]!.id);
                            }}
                          >
                            <span class="aw-set-num" />
                            <span class="flex-1 text-[17px]">{t("ACTIVE_DONE")}</span>
                            <button
                              type="button"
                              class={`aw-set-check ${ex().sets[0]?.done ? "is-done" : ""}`}
                              aria-pressed={ex().sets[0]?.done}
                              aria-label={t("ACTIVE_DONE")}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSetDone(ex().id, ex().sets[0]!.id);
                              }}
                            >
                              {ex().sets[0]?.done ? "✓" : ""}
                            </button>
                          </div>
                        </div>
                      }
                    >
                    {/* Sets — large touch targets */}
                    <div class="aw-focus-sets">
                      <Index each={ex().sets}>
                        {(st, i) => {
                          const isCurrent = () =>
                            firstOpenSet()?.id === st().id;
                          return (
                            <div
                              class={`aw-set-row ${st().done ? "is-done" : ""} ${
                                isCurrent() && !st().done ? "is-current" : ""
                              }`}
                              onClick={(e) => {
                                // Tap row (not on stepper) to toggle done
                                const target = e.target as HTMLElement;
                                if (
                                  target.closest(
                                    "button, input, [data-stepper]",
                                  )
                                ) {
                                  return;
                                }
                                toggleSetDone(ex().id, st().id);
                              }}
                            >
                              <span class="aw-set-num">{i + 1}</span>

                              <div class="aw-set-field" data-stepper>
                                <button
                                  type="button"
                                  class="aw-stepper-btn"
                                  aria-label="−"
                                  onClick={() =>
                                    nudgeWeight(ex().id, st().id, -WEIGHT_STEP)
                                  }
                                >
                                  −
                                </button>
                                <div
                                  class="aw-set-value"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <DecimalField
                                    class="aw-set-input tabular-nums"
                                    value={st().weight}
                                    min={0}
                                    decimals={2}
                                    placeholder="—"
                                    aria-label={t("WEIGHT")}
                                    onChange={(weight) =>
                                      updateSet(ex().id, st().id, { weight })
                                    }
                                  />
                                  <span class="aw-set-unit">
                                    {st().unit}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  class="aw-stepper-btn"
                                  aria-label="+"
                                  onClick={() =>
                                    nudgeWeight(ex().id, st().id, WEIGHT_STEP)
                                  }
                                >
                                  +
                                </button>
                              </div>

                              <div class="aw-set-field" data-stepper>
                                <button
                                  type="button"
                                  class="aw-stepper-btn"
                                  aria-label="−"
                                  onClick={() =>
                                    nudgeReps(ex().id, st().id, -REPS_STEP)
                                  }
                                >
                                  −
                                </button>
                                <div class="aw-set-value">
                                  <input
                                    class="aw-set-input tabular-nums"
                                    type="number"
                                    inputmode="numeric"
                                    min={1}
                                    step={1}
                                    value={st().reps}
                                    onClick={(e) => e.stopPropagation()}
                                    onInput={(e) => {
                                      const n = Math.round(
                                        Number(e.currentTarget.value),
                                      );
                                      if (Number.isFinite(n) && n >= 1) {
                                        updateSet(ex().id, st().id, {
                                          reps: n,
                                        });
                                      }
                                    }}
                                  />
                                  <span class="aw-set-unit">
                                    {t("REPS").slice(0, 3).toLowerCase()}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  class="aw-stepper-btn"
                                  aria-label="+"
                                  onClick={() =>
                                    nudgeReps(ex().id, st().id, REPS_STEP)
                                  }
                                >
                                  +
                                </button>
                              </div>

                              <button
                                type="button"
                                class={`aw-set-check ${st().done ? "is-done" : ""}`}
                                aria-pressed={st().done}
                                aria-label={t("SET_DONE")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSetDone(ex().id, st().id);
                                }}
                              >
                                {st().done ? "✓" : ""}
                              </button>
                            </div>
                          );
                        }}
                      </Index>

                      <div class="flex gap-2 pt-1">
                        <button
                          type="button"
                          class="btn-ghost flex-1 border border-surface-border !py-2.5 text-xs"
                          onClick={() => addSet(ex().id)}
                        >
                          + {t("ADD_SET")}
                        </button>
                        <Show when={ex().sets.length > 1}>
                          <button
                            type="button"
                            class="btn-ghost border border-surface-border !py-2.5 px-4 text-xs text-ink-muted"
                            onClick={() =>
                              removeSet(
                                ex().id,
                                ex().sets[ex().sets.length - 1]!.id,
                              )
                            }
                          >
                            −
                          </button>
                        </Show>
                      </div>
                    </div>

                    </Show>

                    {/* Notes (collapsed) */}
                    <div class="pt-1">
                      <button
                        type="button"
                        class="text-xs font-medium text-ink-muted"
                        onClick={() => setNotesOpen((v) => !v)}
                      >
                        {t("NOTES")} {notesOpen() || ex().notes ? "▾" : "▸"}
                      </button>
                      <Show when={notesOpen() || ex().notes}>
                        <textarea
                          class="input mt-1 min-h-[52px] resize-y text-sm"
                          placeholder={t("EXERCISE_NOTES_PLACEHOLDER")}
                          value={ex().notes}
                          onInput={(e) =>
                            updateExercise(ex().id, {
                              notes: e.currentTarget.value,
                            })
                          }
                        />
                      </Show>
                    </div>

                    {/* Advance hint */}
                    <Show when={showAdvanceHint() && nextExercise()}>
                      <button
                        type="button"
                        class="aw-advance-hint"
                        onClick={() => goToNext()}
                      >
                        {t("NEXT_EXERCISE")}:{" "}
                        <strong>
                          {nextExercise()!.name.trim() || t("EXERCISE")}
                        </strong>
                        <span class="aw-advance-arrow">→</span>
                      </button>
                    </Show>
                  </div>
                );
              }}
            </Show>

            {/* Compact bottom bar: list · next · finish */}
            <div class="active-workout-footer aw-focus-footer">
              <div class="aw-dock">
                <button
                  type="button"
                  class="aw-dock-btn"
                  onClick={() => openList()}
                  aria-label={t("EXERCISE_LIST")}
                >
                  <span class="aw-dock-icon" aria-hidden>
                    ☰
                  </span>
                  <span class="aw-dock-label">{t("EXERCISE_LIST")}</span>
                </button>

                <button
                  type="button"
                  class="aw-dock-btn"
                  onClick={() => addExercise()}
                  aria-label={t("ADD_EXERCISE")}
                >
                  <span class="aw-dock-icon" aria-hidden>
                    +
                  </span>
                  <span class="aw-dock-label">{t("ADD_SHORT")}</span>
                </button>

                <button
                  type="button"
                  class="aw-dock-next"
                  disabled={!nextExercise()}
                  onClick={() => goToNext()}
                  title={
                    nextExercise()
                      ? `${t("NEXT_EXERCISE")}: ${
                          nextExercise()!.name.trim() || t("EXERCISE")
                        }`
                      : undefined
                  }
                >
                  <span class="aw-dock-next-kicker">{t("NEXT_EXERCISE")}</span>
                  <span class="aw-dock-next-name truncate">
                    {nextExercise()
                      ? nextExercise()!.name.trim() || t("EXERCISE")
                      : "—"}
                  </span>
                </button>

                <button
                  type="button"
                  class="aw-dock-finish"
                  disabled={saving()}
                  onClick={() => void finish()}
                >
                  {saving() ? t("LOADING") : t("FINISH_SHORT")}
                </button>
              </div>
            </div>

            {/* Full exercise list sheet */}
            <BottomSheet
              open={listOpen()}
              onClose={() => setListOpen(false)}
              title={t("EXERCISE_LIST")}
            >
              <div
                class={`aw-list-sheet${
                  drag.isDragging() ? " is-list-dragging" : ""
                }`}
              >
                <Show when={cur().exercises.length === 0}>
                  <p class="px-2 py-6 text-center text-sm text-ink-muted">
                    {t("ACTIVE_EMPTY")}
                  </p>
                </Show>

                <Show when={cur().exercises.length > 0}>
                  <p class="aw-circuit-hint px-1">{t("CIRCUIT_HINT")}</p>
                </Show>

                <div
                  class="drag-list aw-list-scroll"
                  ref={(el) => drag.setListRef(el)}
                >
                  <div
                    class="drag-insert-line"
                    ref={drag.setIndicatorRef}
                    aria-hidden="true"
                  />
                  <div class="space-y-2 pb-3">
                    <Index each={cur().exercises}>
                      {(ex, i) => {
                        const isFocus = () =>
                          cur().focusExerciseId === ex().id;
                        const done = () => isExerciseCompleteInList(ex());
                        const inCircuit = () => Boolean(ex().circuitId);
                        const circuit = () =>
                          ex().circuitId
                            ? cur().circuits.find(
                                (c) => c.id === ex().circuitId,
                              )
                            : null;
                        const circuitFirst = () => {
                          if (!ex().circuitId) return false;
                          if (i === 0) return true;
                          return (
                            cur().exercises[i - 1]?.circuitId !==
                            ex().circuitId
                          );
                        };
                        const circuitLast = () => {
                          if (!ex().circuitId) return false;
                          if (i >= cur().exercises.length - 1) return true;
                          return (
                            cur().exercises[i + 1]?.circuitId !==
                            ex().circuitId
                          );
                        };
                        const circuitCount = () => {
                          const cid = ex().circuitId;
                          if (!cid) return 0;
                          return cur().exercises.filter(
                            (e) => e.circuitId === cid,
                          ).length;
                        };
                        const circuitPos = () => {
                          const cid = ex().circuitId;
                          if (!cid) return 0;
                          let n = 0;
                          for (let j = 0; j <= i; j++) {
                            if (cur().exercises[j]?.circuitId === cid) n++;
                          }
                          return n;
                        };
                        return (
                          <div
                            data-drag-index={String(i)}
                            data-ex-id={ex().id}
                            class={`${drag.itemClass(i)}${
                              inCircuit() ? " aw-circuit-row" : ""
                            }${circuitFirst() ? " is-circuit-first" : ""}${
                              circuitLast() ? " is-circuit-last" : ""
                            }${
                              inCircuit() && !circuitFirst() && !circuitLast()
                                ? " is-circuit-mid"
                                : ""
                            }`}
                            ref={(el) => drag.setItemRef(i, el)}
                          >
                            <Show when={circuitFirst()}>
                              <div class="aw-circuit-group-head">
                                <div class="aw-circuit-group-title">
                                  <span class="aw-circuit-group-icon" aria-hidden>
                                    ↻
                                  </span>
                                  <span>{t("CIRCUIT_GROUP_TITLE")}</span>
                                  <span class="aw-circuit-group-meta">
                                    {t("CIRCUIT_GROUP_COUNT").replace(
                                      "{n}",
                                      String(circuitCount()),
                                    )}
                                  </span>
                                </div>
                                <div class="aw-circuit-rounds">
                                  <span class="aw-circuit-rounds-label">
                                    {t("CIRCUIT_ROUNDS_LABEL")}
                                  </span>
                                  <button
                                    type="button"
                                    class="aw-stepper-btn !min-h-8 !min-w-8 !text-sm"
                                    onClick={() =>
                                      setCircuitRounds(
                                        ex().circuitId!,
                                        (circuit()?.rounds ?? 3) - 1,
                                      )
                                    }
                                  >
                                    −
                                  </button>
                                  <span class="aw-circuit-rounds-val tabular-nums">
                                    {circuit()?.rounds ?? 3}
                                  </span>
                                  <button
                                    type="button"
                                    class="aw-stepper-btn !min-h-8 !min-w-8 !text-sm"
                                    onClick={() =>
                                      setCircuitRounds(
                                        ex().circuitId!,
                                        (circuit()?.rounds ?? 3) + 1,
                                      )
                                    }
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            </Show>

                            <div
                              class={`aw-list-item ${isFocus() ? "is-current" : ""} ${done() ? "is-done" : ""} ${
                                inCircuit() ? "is-in-circuit" : ""
                              }`}
                            >
                              <DragHandle {...drag.handleProps(i)} />
                              <button
                                type="button"
                                class="min-w-0 flex-1 text-left"
                                onClick={() => goToExerciseFromList(ex().id)}
                              >
                                <div class="flex items-center gap-1.5">
                                  <span class="truncate text-sm font-semibold text-ink">
                                    {ex().name.trim() || t("EXERCISE")}
                                  </span>
                                  <Show when={inCircuit()}>
                                    <span class="aw-circuit-badge">
                                      {t("CIRCUIT_BADGE")} {circuitPos()}/
                                      {circuitCount()}
                                    </span>
                                  </Show>
                                </div>
                                <div class="mt-0.5 text-[11px] text-ink-muted tabular-nums">
                                  <Show
                                    when={activeType(ex()) === "strength"}
                                    fallback={
                                      <>
                                        {metricParts({
                                          exerciseType: activeType(ex()),
                                          durationSec: ex().durationSec,
                                          distanceM: ex().distanceM,
                                          intensity: ex().intensity,
                                          rounds: ex().rounds,
                                        }).join(" · ") || "—"}
                                      </>
                                    }
                                  >
                                    {ex().sets.filter((st) => st.done).length}/
                                    {ex().sets.length} {t("SETS").toLowerCase()}
                                  </Show>
                                  <Show when={(ex().bankedSets || 0) > 0}>
                                    <span>
                                      {" "}
                                      · +{ex().bankedSets}{" "}
                                      {t("CIRCUIT_BANKED")}
                                    </span>
                                  </Show>
                                </div>
                              </button>
                              <Show when={done()}>
                                <span class="aw-list-check" aria-hidden>
                                  ✓
                                </span>
                              </Show>
                              <button
                                type="button"
                                class="aw-list-edit-btn"
                                title={t("EDIT_EXERCISE")}
                                aria-label={t("EDIT_EXERCISE")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditor(ex().id);
                                }}
                              >
                                ✎
                              </button>
                              <button
                                type="button"
                                class={`aw-circuit-chip ${
                                  inCircuit() ? "is-on" : ""
                                }`}
                                title={
                                  inCircuit()
                                    ? t("CIRCUIT_LEAVE")
                                    : t("CIRCUIT_JOIN")
                                }
                                onClick={() => toggleInCircuit(ex().id)}
                              >
                                {inCircuit()
                                  ? t("CIRCUIT_IN")
                                  : t("CIRCUIT_JOIN")}
                              </button>
                              <Show when={i > 0 && !inCircuit()}>
                                <button
                                  type="button"
                                  class="aw-circuit-chip aw-circuit-chip--link"
                                  title={t("LINK_CIRCUIT")}
                                  onClick={() => linkWithPrevious(ex().id)}
                                >
                                  {t("CIRCUIT_LINK_PREV")}
                                </button>
                              </Show>
                              <button
                                type="button"
                                class="btn-ghost shrink-0 !px-2 !py-1 text-red-400/70"
                                aria-label={t("DELETE")}
                                onClick={() => void removeExercise(ex().id)}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        );
                      }}
                    </Index>
                  </div>
                </div>

                <div class="aw-list-sheet-actions">
                  <button
                    type="button"
                    class="btn-secondary w-full !py-2.5 text-sm"
                    onClick={() => addExercise()}
                  >
                    + {t("ADD_EXERCISE")}
                  </button>
                  <div class="flex gap-2">
                    <button
                      type="button"
                      class="btn-ghost flex-1 border border-surface-border !py-2 text-xs"
                      disabled={playIndex() <= 0}
                      onClick={() => {
                        goToPrev();
                        setListOpen(false);
                      }}
                    >
                      ← {t("PREV")}
                    </button>
                    <button
                      type="button"
                      class="btn-ghost flex-1 border border-surface-border !py-2 text-xs"
                      disabled={!nextStep()}
                      onClick={() => {
                        goToNext();
                        setListOpen(false);
                      }}
                    >
                      {t("NEXT")} →
                    </button>
                  </div>
                </div>
              </div>
            </BottomSheet>

            <Show when={editKey()} keyed>
              {(key) => {
                const isNew = key === "new";
                const initial = isNew
                  ? createBlankExercise()
                  : cur().exercises.find((e) => e.id === key)!;
                return (
                  <ExerciseFullScreen
                    initial={initial}
                    isNew={isNew}
                    history={(name) =>
                      historyCache()[name.trim().toLowerCase()] ?? []
                    }
                    onNeedHistory={(name) => void loadHistory(name)}
                    onSave={(ex) => saveEditor(isNew, ex)}
                    onDelete={isNew ? undefined : deleteFromEditor}
                    onClose={closeEditor}
                  />
                );
              }}
            </Show>
          </>
        )}
      </Show>
    </div>
  );
}
